import { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import loader from '../../../assets/Images/loader.gif';

const RC_DETAILS_API  = 'https://oneml.spectra.co/getRcDetails';
const RC_UPDATE_API   = 'https://oneml.spectra.co/crm/upsertRCDetails';
const RC_BULK_API     = 'https://oneml.spectra.co/bulkUpsertRCDetails';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const BULK_HEADERS = [
    'serviceID', 'houseName', 'cityID', 'cityName', 'rcName',
    'rcContactNumber', 'rcEmailID', 'cmName', 'cmContactNumber', 'cmEmailID', 'status',
];

const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return { rows: [], error: 'File is empty or has no data rows.' };
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    const rows = lines.slice(1)
        .map((line, i) => {
            const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
            const obj = { _rowIndex: i + 2 };
            headers.forEach((h, j) => { obj[h] = values[j] ?? ''; });
            return obj;
        })
        .filter((row) => BULK_HEADERS.some((h) => row[h]));
    return { rows, error: null };
};

const validateBulkRow = (row) => {
    const errs = [];
    if (!row.serviceID) errs.push('serviceID is required');
    if (row.rcContactNumber && row.rcContactNumber.replace(/\D/g, '').length < 10)
        errs.push('rcContactNumber must be ≥10 digits');
    if (row.cmContactNumber && row.cmContactNumber.replace(/\D/g, '').length < 10)
        errs.push('cmContactNumber must be ≥10 digits');
    if (row.rcEmailID && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.rcEmailID))
        errs.push('rcEmailID is not valid');
    if (row.cmEmailID && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.cmEmailID))
        errs.push('cmEmailID is not valid');
    if (row.status !== '' && !['0', '1'].includes(String(row.status)))
        errs.push('status must be 0 or 1');
    return errs;
};

const EDITABLE_FIELDS = [
    { key: 'houseName',        label: 'House Name' },
    { key: 'cityID',           label: 'City ID' },
    { key: 'cityName',         label: 'City Name' },
    { key: 'rcName',           label: 'RC Name' },
    { key: 'rcContactNumber',  label: 'RC Contact Number' },
    { key: 'rcEmailID',        label: 'RC Email ID' },
    { key: 'cmName',           label: 'CM Name' },
    { key: 'cmContactNumber',  label: 'CM Contact Number' },
    { key: 'cmEmailID',        label: 'CM Email ID' },
];

const StatusBadge = ({ value }) => {
    const active = value === 1 || value === '1';
    return (
        <span className={`badge ${active ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.75rem' }}>
            {active ? 'Active' : 'Inactive'}
        </span>
    );
};

const empty = (v) => v === '' || v === null || v === undefined;

const matchesSearch = (record, query) => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    return (record.serviceID || '').toLowerCase().includes(q)
        || String(record.rcContactNumber || '').toLowerCase().includes(q);
};

/* ─── Edit Modal ─────────────────────────────────────────────── */
const EditModal = ({ record, onClose, onSave, isSaving }) => {
    const [form, setForm]     = useState({ ...record });
    const [errors, setErrors] = useState({});
    const firstRef = useRef(null);

    useEffect(() => { firstRef.current?.focus(); }, []);

    const validate = (key, value) => {
        if (key === 'rcContactNumber' || key === 'cmContactNumber') {
            const digits = value.replace(/\D/g, '');
            if (value && digits.length < 10)
                return 'Must be at least 10 digits';
        }
        if (key === 'rcEmailID' || key === 'cmEmailID') {
            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
                return 'Enter a valid email address';
        }
        return '';
    };

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: validate(key, value) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        ['rcContactNumber', 'cmContactNumber', 'rcEmailID', 'cmEmailID'].forEach((key) => {
            const err = validate(key, form[key] || '');
            if (err) newErrors[key] = err;
        });
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        onSave(form);
    };

    return (
        <div
            className="modal d-block"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '520px' }}>
                <div className="modal-content" style={{ borderRadius: '10px' }}>

                    {/* Header */}
                    <div
                        className="modal-header py-2 px-3"
                        style={{ backgroundColor: 'var(--bg-color)', borderBottom: 'none' }}
                    >
                        <div>
                            <h6 className="modal-title text-white mb-0" style={{ fontWeight: 600 }}>
                                <i className="fa-solid fa-pen-to-square me-2" />
                                Edit RC Details
                            </h6>
                            <small className="text-white" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                Service ID: {record.serviceID || '—'}
                            </small>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={isSaving}
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body p-3">
                        <form id="rc-edit-form" onSubmit={handleSubmit}>

                            {/* Read-only info strip */}
                            <div
                                className="d-flex gap-3 mb-3 p-2 rounded"
                                style={{ backgroundColor: '#f3edf9', fontSize: '0.8rem' }}
                            >
                                <span><strong>RC ID:</strong> {record.rcID}</span>
                                <span><strong>Service ID:</strong> {record.serviceID || '—'}</span>
                            </div>

                            <div className="row g-2">
                                {EDITABLE_FIELDS.map(({ key, label }, i) => (
                                    <div className="col-12 col-sm-6" key={key}>
                                        <label
                                            htmlFor={`edit-${key}`}
                                            style={{ fontSize: '0.74rem', fontWeight: 600, color: '#555', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                                        >
                                            {label}
                                        </label>
                                        <input
                                            id={`edit-${key}`}
                                            ref={i === 0 ? firstRef : null}
                                            type={key.toLowerCase().includes('email') ? 'email' : 'text'}
                                            className={`form-control form-control-sm ${errors[key] ? 'is-invalid' : ''}`}
                                            value={form[key] || ''}
                                            onChange={(e) => handleChange(key, e.target.value)}
                                            disabled={isSaving}
                                            style={{ fontSize: '0.85rem', borderColor: errors[key] ? '#dc3545' : '#d9cce8' }}
                                        />
                                        {errors[key] && (
                                            <div style={{ color: '#dc3545', fontSize: '0.72rem', marginTop: '2px' }}>
                                                <i className="fa-solid fa-circle-exclamation me-1" />{errors[key]}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Status toggle */}
                                <div className="col-12 mt-1">
                                    <label style={{ fontSize: '0.74rem', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Status
                                    </label>
                                    <div className="d-flex gap-3 mt-1">
                                        {[{ val: 1, label: 'Active' }, { val: 0, label: 'Inactive' }].map(({ val, label }) => (
                                            <label key={val} className="d-flex align-items-center gap-1" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                                                <input
                                                    type="radio"
                                                    name="status"
                                                    value={val}
                                                    checked={Number(form.status) === val}
                                                    onChange={() => handleChange('status', val)}
                                                    disabled={isSaving}
                                                />
                                                <span className={`badge ${val === 1 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.75rem' }}>
                                                    {label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Footer — outside the form; Save uses form="rc-edit-form" to trigger submit */}
                    <div className="modal-footer py-2 px-3" style={{ borderTop: '1px solid #ede4f7' }}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{ backgroundColor: '#ede4f7', color: 'var(--bg-color)', border: 'none', borderRadius: '6px' }}
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="rc-edit-form"
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: 'var(--bg-color)', border: 'none', borderRadius: '6px', minWidth: '80px' }}
                            disabled={isSaving}
                        >
                            {isSaving
                                ? <><span className="spinner-border spinner-border-sm me-1" role="status" /> Saving…</>
                                : <><i className="fa-solid fa-floppy-disk me-1" />Save</>}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ─── Bulk Upload Modal ──────────────────────────────────────── */
const BulkUploadModal = ({ onClose, onComplete }) => {
    const [step, setStep]               = useState('idle');   // idle | preview | uploading | done
    const [parsedRows, setParsedRows]   = useState([]);
    const [rowErrors, setRowErrors]     = useState({});
    const [result, setResult]           = useState(null);     // { success, message, data }
    const fileRef                       = useRef(null);

    const downloadTemplate = () => {
        const example = 'SVC001,House A,CITY01,Mumbai,John RC,9876543210,rc@example.com,Jane CM,9876543211,cm@example.com,1';
        const csv = `${BULK_HEADERS.join(',')}\n${example}`;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = 'rc_bulk_upload_template.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const handleFile = (file) => {
        if (!file || !file.name.toLowerCase().endsWith('.csv')) {
            toast.warning('Please upload a .csv file.', { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000 });
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const { rows, error } = parseCSV(e.target.result);
            if (error) { toast.error(error, { position: toast.POSITION.BOTTOM_CENTER, autoClose: 3000 }); return; }
            if (rows.length === 0) { toast.warning('No data rows found.', { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000 }); return; }
            const errors = {};
            rows.forEach((row, i) => { const e = validateBulkRow(row); if (e.length) errors[i] = e; });
            setParsedRows(rows);
            setRowErrors(errors);
            setStep('preview');
        };
        reader.readAsText(file);
    };

    const validRows   = parsedRows.filter((_, i) => !rowErrors[i]);
    const invalidRows = parsedRows.filter((_, i) =>  rowErrors[i]);

    const handleUpload = async () => {
        if (validRows.length === 0) return;
        setStep('uploading');
        const userName = sessionStorage.getItem('userEmail');
        const payload  = validRows.map((row) => ({
            serviceID:       row.serviceID,
            rcContactNumber: row.rcContactNumber  || '',
            cityID:          row.cityID           || '',
            cityName:        row.cityName         || '',
            houseName:       row.houseName        || '',
            rcName:          row.rcName           || '',
            rcEmailID:       row.rcEmailID        || '',
            cmName:          row.cmName           || '',
            cmContactNumber: row.cmContactNumber  || '',
            cmEmailID:       row.cmEmailID        || '',
            status:          row.status !== '' ? String(row.status) : '1',
            userName,
        }));
        try {
            const response = await fetch(RC_BULK_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ records: payload }),
            });
            const res = await response.json();
            setResult({ success: res?.meta?.Status === true, message: res?.meta?.Message || '', data: res?.data || [] });
        } catch {
            setResult({ success: false, message: 'Network error — please try again.', data: [] });
        }
        setStep('done');
        onComplete();
    };

    const resetFile = () => { setParsedRows([]); setRowErrors({}); setStep('idle'); };

    return (
        <div
            className="modal d-block"
            style={{ background: 'rgba(0,0,0,0.45)'}}
            onClick={(e) => { if (e.target === e.currentTarget && step !== 'uploading') onClose(); }}
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '720px' }}>
                <div className="modal-content" style={{ borderRadius: '10px' }}>

                    {/* Header */}
                    <div className="modal-header py-2 px-3" style={{ backgroundColor: 'var(--bg-color)', borderBottom: 'none' }}>
                        <div>
                            <h6 className="modal-title text-white mb-0" style={{ fontWeight: 600 }}>
                                <i className="fa-solid fa-cloud-arrow-up me-2" />Bulk Upload RC Details
                            </h6>
                            <small className="text-white" style={{ opacity: 0.8, fontSize: '0.75rem' }}>
                                Upload a CSV to add or update multiple records at once
                            </small>
                        </div>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={step === 'uploading'} />
                    </div>

                    {/* Body */}
                    <div className="modal-body p-3">

                        {/* IDLE */}
                        {step === 'idle' && (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="mb-0" style={{ fontSize: '0.85rem', color: '#555' }}>
                                        Download the template, fill in your data, then upload the file.
                                    </p>
                                    <button
                                        className="btn btn-sm"
                                        style={{ backgroundColor: '#f0eaf8', color: 'var(--bg-color)', border: 'none', borderRadius: '6px', whiteSpace: 'nowrap' }}
                                        onClick={downloadTemplate}
                                    >
                                        <i className="fa-solid fa-download me-1" />Download Template
                                    </button>
                                </div>
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center p-4 rounded"
                                    style={{ border: '2px dashed #d9cce8', backgroundColor: '#faf7fd', minHeight: '160px', cursor: 'pointer' }}
                                    onClick={() => fileRef.current?.click()}
                                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                                    onDragOver={(e) => e.preventDefault()}
                                >
                                    <i className="fa-solid fa-file-csv fa-2x mb-2" style={{ color: 'var(--bg-color)', opacity: 0.6 }} />
                                    <p className="mb-1" style={{ fontSize: '0.88rem', color: '#666' }}>
                                        Drag & drop a CSV file here, or <span style={{ color: 'var(--bg-color)', fontWeight: 600 }}>browse</span>
                                    </p>
                                    <small style={{ color: '#aaa' }}>Only .csv files accepted</small>
                                    <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files[0])} />
                                </div>
                            </>
                        )}

                        {/* PREVIEW */}
                        {step === 'preview' && (
                            <>
                                <div className="d-flex gap-2 mb-3 flex-wrap">
                                    <span className="badge bg-success" style={{ fontSize: '0.8rem', padding: '5px 10px' }}>
                                        <i className="fa-solid fa-check me-1" />{validRows.length} valid
                                    </span>
                                    {invalidRows.length > 0 && (
                                        <span className="badge bg-danger" style={{ fontSize: '0.8rem', padding: '5px 10px' }}>
                                            <i className="fa-solid fa-xmark me-1" />{invalidRows.length} with errors (will be skipped)
                                        </span>
                                    )}
                                </div>
                                <div style={{ maxHeight: '320px', overflowY: 'auto', fontSize: '0.76rem' }}>
                                    <table className="table table-bordered table-sm mb-0">
                                        <thead>
                                            <tr style={{ backgroundColor: 'var(--bg-color)', color: '#fff' }}>
                                                <th style={{ padding: '5px 8px' }}>Row</th>
                                                {BULK_HEADERS.map((h) => <th key={h} style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{h}</th>)}
                                                <th style={{ padding: '5px 8px' }}>Validation</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedRows.map((row, i) => {
                                                const hasErr = !!rowErrors[i];
                                                return (
                                                    <tr key={i} style={{ backgroundColor: hasErr ? '#fff5f5' : 'inherit' }}>
                                                        <td style={{ padding: '4px 8px' }}>{row._rowIndex}</td>
                                                        {BULK_HEADERS.map((h) => <td key={h} style={{ padding: '4px 8px' }}>{row[h] || '—'}</td>)}
                                                        <td style={{ padding: '4px 8px' }}>
                                                            {hasErr
                                                                ? <span title={rowErrors[i].join('; ')} style={{ color: '#dc3545', cursor: 'help' }}>
                                                                    <i className="fa-solid fa-circle-exclamation me-1" />{rowErrors[i][0]}
                                                                  </span>
                                                                : <span style={{ color: '#198754' }}><i className="fa-solid fa-circle-check" /></span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    className="btn btn-sm mt-2"
                                    style={{ backgroundColor: '#f0eaf8', color: 'var(--bg-color)', border: 'none', borderRadius: '6px' }}
                                    onClick={resetFile}
                                >
                                    <i className="fa-solid fa-arrow-left me-1" />Change File
                                </button>
                            </>
                        )}

                        {/* UPLOADING */}
                        {step === 'uploading' && (
                            <div className="text-center py-4">
                                <p style={{ fontSize: '0.9rem', color: '#555' }}>Uploading {validRows.length} records…</p>
                                <div className="d-flex justify-content-center">
                                    <span className="spinner-border" style={{ color: 'var(--bg-color)' }} role="status" />
                                </div>
                                <small className="text-muted mt-2 d-block">Please wait, do not close this window.</small>
                            </div>
                        )}

                        {/* DONE */}
                        {step === 'done' && result && (
                            <>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    {result.success
                                        ? <span className="badge bg-success" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                            <i className="fa-solid fa-circle-check me-1" />Upload Successful
                                          </span>
                                        : <span className="badge bg-danger" style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                                            <i className="fa-solid fa-circle-xmark me-1" />Upload Failed
                                          </span>}
                                    <span style={{ fontSize: '0.85rem', color: '#555' }}>{result.message}</span>
                                </div>
                                {result.data && result.data.length > 0 && (
                                    <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '0.78rem' }}>
                                        <table className="table table-bordered table-sm mb-0">
                                            <thead>
                                                <tr style={{ backgroundColor: 'var(--bg-color)', color: '#fff' }}>
                                                    <th style={{ padding: '5px 8px' }}>Service ID</th>
                                                    <th style={{ padding: '5px 8px' }}>Result</th>
                                                    <th style={{ padding: '5px 8px' }}>Message</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.data.map((r, i) => (
                                                    <tr key={i} style={{ backgroundColor: r.status === 'success' ? '#f0fff4' : '#fff5f5' }}>
                                                        <td style={{ padding: '4px 8px' }}>{r.serviceID || '—'}</td>
                                                        <td style={{ padding: '4px 8px' }}>
                                                            {r.status === 'success'
                                                                ? <span style={{ color: '#198754' }}><i className="fa-solid fa-circle-check me-1" />Success</span>
                                                                : <span style={{ color: '#dc3545' }}><i className="fa-solid fa-circle-xmark me-1" />Failed</span>}
                                                        </td>
                                                        <td style={{ padding: '4px 8px', color: '#555' }}>{r.message || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer py-2 px-3" style={{ borderTop: '1px solid #ede4f7' }}>
                        {step !== 'uploading' && (
                            <button
                                type="button"
                                className="btn btn-sm"
                                style={{ backgroundColor: '#ede4f7', color: 'var(--bg-color)', border: 'none', borderRadius: '6px' }}
                                onClick={onClose}
                            >
                                {step === 'done' ? 'Close' : 'Cancel'}
                            </button>
                        )}
                        {step === 'preview' && validRows.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm text-white"
                                style={{ backgroundColor: 'var(--bg-color)', border: 'none', borderRadius: '6px', minWidth: '120px' }}
                                onClick={handleUpload}
                            >
                                <i className="fa-solid fa-cloud-arrow-up me-1" />Upload {validRows.length} Records
                            </button>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────────── */
const RcDetailsCard = () => {
    const [searchInput, setSearchInput] = useState('');
    const [allData, setAllData]         = useState([]);
    const [isLoading, setIsLoading]     = useState(false);
    const [hasFetched, setHasFetched]   = useState(false);
    const [page, setPage]               = useState(1);
    const [pageSize, setPageSize]       = useState(10);
    const [editRecord, setEditRecord]       = useState(null);
    const [isSaving, setIsSaving]           = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(RC_DETAILS_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                const result = await response.json();
                if (result?.meta?.Status === true) {
                    setAllData(result.data || []);
                } else {
                    toast.warning(result?.meta?.Message || 'No data found', {
                        position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                    });
                }
            } catch (error) {
                console.error('getRcDetails error:', error);
                toast.error('Failed to fetch RC details.', {
                    position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                });
            } finally {
                setIsLoading(false);
                setHasFetched(true);
            }
        };
        fetchAllData();
    }, []);

    useEffect(() => { setPage(1); }, [searchInput, pageSize]);

    const handleUpdate = async (updatedRecord) => {
        try {
            setIsSaving(true);
            const response = await fetch(RC_UPDATE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    serviceID:        updatedRecord.serviceID,
                    rcContactNumber:  updatedRecord.rcContactNumber  || '',
                    cityID:           updatedRecord.cityID           || '',
                    cityName:         updatedRecord.cityName         || '',
                    houseName:        updatedRecord.houseName        || '',
                    rcName:           updatedRecord.rcName           || '',
                    rcEmailID:        updatedRecord.rcEmailID        || '',
                    cmName:           updatedRecord.cmName           || '',
                    cmContactNumber:  updatedRecord.cmContactNumber  || '',
                    cmEmailID:        updatedRecord.cmEmailID        || '',
                    status:           String(updatedRecord.status),
                    userName : sessionStorage.getItem('userEmail')
                }),
            });
            const result = await response.json();
            if (result?.meta?.Status === true) {
                setAllData((prev) =>
                    prev.map((r) => (r.serviceID === updatedRecord.serviceID ? { ...r, ...updatedRecord } : r))
                );
                toast.success(result?.meta?.Message || 'Updated successfully', {
                    position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                });
                setEditRecord(null);
            } else {
                toast.warning(result?.meta?.Message || 'Update failed', {
                    position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                });
            }
        } catch (error) {
            console.error('updateRcDetails error:', error);
            toast.error('Failed to update. Please try again.', {
                position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const filteredData = useMemo(
        () => allData.filter((r) => matchesSearch(r, searchInput)),
        [allData, searchInput]
    );

    const totalPages  = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const pageData    = filteredData.slice((page - 1) * pageSize, page * pageSize);
    const startRecord = filteredData.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRecord   = Math.min(page * pageSize, filteredData.length);

    const pageNumbers = useMemo(() => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
                pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
            }
        }
        return pages;
    }, [totalPages, page]);

    return (
        <>
            {(isLoading) && (
                <div className="loader-wrapper">
                    <div className="loader">
                        <img src={loader} alt="loading..." />
                    </div>
                </div>
            )}

            {/* Edit modal */}
            {editRecord && (
                <EditModal
                    record={editRecord}
                    onClose={() => setEditRecord(null)}
                    onSave={handleUpdate}
                    isSaving={isSaving}
                />
            )}

            {/* Bulk upload modal */}
            {showBulkUpload && (
                <BulkUploadModal
                    onClose={() => setShowBulkUpload(false)}
                    onComplete={async () => {
                        try {
                            setIsLoading(true);
                            const response = await fetch(RC_DETAILS_API, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
                            const result   = await response.json();
                            if (result?.meta?.Status === true) setAllData(result.data || []);
                        } catch { /* keep stale data */ }
                        finally { setIsLoading(false); }
                    }}
                />
            )}

            {/* Search bar */}
            <div className="container-fluid p-2 Searchbar-bg" style={{ marginTop: '63px', position: 'sticky', top: '63px', zIndex: 999 }}>
                <div className="d-flex justify-content-center align-items-center gap-2">
                    <div className="position-relative" style={{ flex: 1, maxWidth: '400px' }}>
                        <input
                            type="text"
                            className="search-box p-1 border-0 w-100"
                            placeholder="Search by Service ID or RC Contact..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ paddingRight: '28px', borderRadius: '6px' }}
                        />
                        {searchInput && (
                            <button
                                className="btn btn-sm p-0 position-absolute"
                                style={{ right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#999' }}
                                onClick={() => setSearchInput('')}
                                title="Clear"
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div>
                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'var(--bg-color)' }} />
                    <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: 'var(--bg-color)', border: 'none', borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.82rem' }}
                        onClick={() => setShowBulkUpload(true)}
                        title="Bulk Upload"
                    >
                        <i className="fa-solid fa-cloud-arrow-up me-1" />Bulk Upload
                    </button>
                </div>
            </div>

            {/* Table area */}
            <div className="container-fluid p-2 mt-1">

                {/* Toolbar */}
                {hasFetched && !isLoading && allData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <small className="text-muted">
                            {filteredData.length === 0
                                ? 'No records match'
                                : `Showing ${startRecord}–${endRecord} of ${filteredData.length}${searchInput ? ` (filtered from ${allData.length})` : ''}`}
                        </small>
                        <div className="d-flex align-items-center gap-1">
                            <small className="text-muted me-1">Rows:</small>
                            {PAGE_SIZE_OPTIONS.map((s) => (
                                <button
                                    key={s}
                                    className="btn btn-sm"
                                    style={{
                                        padding: '1px 8px', fontSize: '0.78rem', borderRadius: '4px', border: 'none',
                                        backgroundColor: pageSize === s ? 'var(--bg-color)' : '#f0eaf8',
                                        color: pageSize === s ? '#fff' : 'var(--bg-color)',
                                    }}
                                    onClick={() => setPageSize(s)}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* No data */}
                {hasFetched && !isLoading && allData.length === 0 && (
                    <div className="text-center text-muted mt-5">
                        <i className="fa-solid fa-circle-info fa-2x mb-2" style={{ color: 'var(--bg-color)', opacity: 0.5 }} />
                        <p style={{ fontSize: '0.95rem' }}>No RC details available.</p>
                    </div>
                )}

                {/* Table */}
                {hasFetched && !isLoading && allData.length > 0 && (
                    <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                        <table className="table table-bordered table-hover mb-0" style={{ fontSize: '0.83rem' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--bg-color)', color: '#fff' }}>
                                    <th style={thStyle}>#</th>
                                    <th style={thStyle}>RC ID</th>
                                    <th style={thStyle}>Service ID</th>
                                    <th style={thStyle}>House Name</th>
                                    <th style={thStyle}>City</th>
                                    <th style={thStyle}>RC Name</th>
                                    <th style={thStyle}>RC Contact</th>
                                    <th style={thStyle}>RC Email</th>
                                    <th style={thStyle}>CM Name</th>
                                    <th style={thStyle}>CM Contact</th>
                                    <th style={thStyle}>CM Email</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageData.length === 0 ? (
                                    <tr>
                                        <td colSpan={13} className="text-center text-muted py-4">
                                            No records match your search.
                                        </td>
                                    </tr>
                                ) : (
                                    pageData.map((r, idx) => (
                                        <tr key={r.rcID} style={{ verticalAlign: 'middle' }}>
                                            <td style={tdStyle}>{startRecord + idx}</td>
                                            <td style={tdStyle}>{r.rcID}</td>
                                            <td style={tdStyle}>{empty(r.serviceID) ? '—' : r.serviceID}</td>
                                            <td style={tdStyle}>{empty(r.houseName) ? '—' : r.houseName}</td>
                                            <td style={tdStyle}>{empty(r.cityName) ? '—' : r.cityName}</td>
                                            <td style={tdStyle}>{empty(r.rcName) ? '—' : r.rcName}</td>
                                            <td style={tdStyle}>{empty(r.rcContactNumber) ? '—' : r.rcContactNumber}</td>
                                            <td style={tdStyle}>{empty(r.rcEmailID) ? '—' : r.rcEmailID}</td>
                                            <td style={tdStyle}>{empty(r.cmName) ? '—' : r.cmName}</td>
                                            <td style={tdStyle}>{empty(r.cmContactNumber) ? '—' : r.cmContactNumber}</td>
                                            <td style={tdStyle}>{empty(r.cmEmailID) ? '—' : r.cmEmailID}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <StatusBadge value={r.status} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <button
                                                    className="btn btn-sm"
                                                    title="Edit"
                                                    style={{ backgroundColor: '#f0eaf8', border: 'none', borderRadius: '5px', padding: '3px 8px' }}
                                                    onClick={() => setEditRecord(r)}
                                                >
                                                    <i className="fa-solid fa-pen" style={{ color: 'var(--bg-color)', fontSize: '0.78rem' }} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {hasFetched && !isLoading && filteredData.length > 0 && totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center mt-3 gap-1 flex-wrap">
                        <button className="btn btn-sm" style={pagerBtn(false, page === 1)} onClick={() => setPage(1)} disabled={page === 1} title="First">
                            <i className="fa-solid fa-angles-left" />
                        </button>
                        <button className="btn btn-sm" style={pagerBtn(false, page === 1)} onClick={() => setPage((p) => p - 1)} disabled={page === 1} title="Previous">
                            <i className="fa-solid fa-angle-left" />
                        </button>

                        {pageNumbers.map((p, i) =>
                            p === '...' ? (
                                <span key={`e-${i}`} className="px-1 text-muted" style={{ fontSize: '0.85rem' }}>…</span>
                            ) : (
                                <button key={p} className="btn btn-sm" style={pagerBtn(p === page, false)} onClick={() => setPage(p)}>{p}</button>
                            )
                        )}

                        <button className="btn btn-sm" style={pagerBtn(false, page === totalPages)} onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} title="Next">
                            <i className="fa-solid fa-angle-right" />
                        </button>
                        <button className="btn btn-sm" style={pagerBtn(false, page === totalPages)} onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last">
                            <i className="fa-solid fa-angles-right" />
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

const thStyle = {
    whiteSpace: 'nowrap',
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: '0.8rem',
    letterSpacing: '0.03em',
    border: 'none',
};

const tdStyle = {
    whiteSpace: 'nowrap',
    padding: '7px 10px',
    color: '#333',
};

const pagerBtn = (active, disabled) => ({
    padding: '3px 9px',
    fontSize: '0.78rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: active ? 'var(--bg-color)' : disabled ? '#e9e9e9' : '#f0eaf8',
    color: active ? '#fff' : disabled ? '#aaa' : 'var(--bg-color)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    minWidth: '30px',
});

export default RcDetailsCard;
